import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, CATEGORIES } from "@/lib/api";
import { ArticleCard } from "@/components/ArticleCard";
import { RefreshButton } from "@/components/RefreshButton";
import { SponsoredCard } from "@/components/SponsoredCard";
import { useAds, sponsoredAt } from "@/lib/ads";
import { useI18n } from "@/i18n";

export default function Latest() {
  const { t, catName } = useI18n();
  const { data: ads } = useAds();
  const [category, setCategory] = useState("latest");
  const [sourceId, setSourceId] = useState("");
  const [sinceDays, setSinceDays] = useState("");
  const [page, setPage] = useState(0);
  const [items, setItems] = useState([]);
  const limit = 12;

  const { data: sources } = useQuery({ queryKey: ["pub-sources"], queryFn: async () => (await api.get("/sources")).data });

  useEffect(() => { setPage(0); }, [category, sourceId, sinceDays]);

  const params = new URLSearchParams({ skip: page * limit, limit });
  if (category !== "latest") params.set("category", category);
  if (sourceId) params.set("source_id", sourceId);
  if (sinceDays) params.set("since_days", sinceDays);
  const paramStr = params.toString();

  const { data, isLoading } = useQuery({
    queryKey: ["latest-feed", paramStr],
    queryFn: async () => {
      const res = (await api.get(`/articles?${paramStr}`)).data;
      setItems((prev) => (page === 0 ? res.items : [...prev, ...res.items]));
      return res;
    },
  });

  const selCls = "px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg outline-none focus:border-violet-300";

  return (
    <div className="pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-2">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">{t("latest.title")}</h1>
            <p className="mt-2 text-slate-600 max-w-2xl">{t("latest.desc")}</p>
          </div>
          <div className="pt-1"><RefreshButton /></div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-wrap gap-3 mb-6">
          <select data-testid="filter-category" value={category} onChange={(e) => setCategory(e.target.value)} className={selCls}>
            {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{catName(c.slug)}</option>)}
          </select>
          <select data-testid="filter-source" value={sourceId} onChange={(e) => setSourceId(e.target.value)} className={selCls}>
            <option value="">{t("filter.allSources")}</option>
            {sources?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select data-testid="filter-date" value={sinceDays} onChange={(e) => setSinceDays(e.target.value)} className={selCls}>
            <option value="">{t("filter.anyTime")}</option>
            <option value="1">{t("filter.24h")}</option>
            <option value="3">{t("filter.3d")}</option>
            <option value="7">{t("filter.7d")}</option>
          </select>
        </div>

        {items.length === 0 && !isLoading && (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-muted-foreground">{t("common.noStories")}</div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((a, i) => (
            <React.Fragment key={a.id}>
              {sponsoredAt(i, ads) !== undefined && <SponsoredCard card={sponsoredAt(i, ads)} />}
              <ArticleCard article={a} index={i} />
            </React.Fragment>
          ))}
        </div>
        {data?.has_more && (
          <div className="mt-8 text-center">
            <button data-testid="load-more-btn" onClick={() => setPage((p) => p + 1)} className="px-6 py-2.5 bg-white border border-slate-300 rounded-full text-sm font-medium hover:border-violet-300 hover:text-violet-700 transition-colors">{t("common.loadMore")}</button>
          </div>
        )}
      </div>
    </div>
  );
}
